import {createRouter, createWebHistory, type Router, type RouteRecordRaw, type LocationQueryRaw} from "vue-router";
import type {FormEnvironment, NetworkResponse} from "@fd/Types";
import type {RouteLocationNormalizedLoaded} from "vue-router";
import type {MenuItem, MenuSelect} from "@t/ui/Menu";
import {parseEndpoint} from "@/dev/api/Endpoint";
import {isNav, hasChildren} from "@t/ui/Menu";
import resolvePageComponent from "./PageResolver";
import {closeAll} from "@/ui/paneltree/PanelTree.ts";
import {AppContextMap, emptyMap} from "@t/fd/AppContext.ts";
import {urlParam} from "@fd/NetLib.ts";

export interface StrutsRoute {
	path: string;
	/** Vue component */
	component?: string;
	/** Active section - used to render top navigation */
	section?: string;
	param?: string;
	/**
	 * Binding for the Vue component
	 */
	bind?: {
		[key:string]: string | number
	}
}

/** Utility function for unwrapping query params if provided in multiple instances (it takes the first) */
export function param(value?:string|string[]):string {
	if (Array.isArray(value)) {
		if (value.length > 0) return value[0];
		return null;
	}
	return value;
}

export interface StrutsRoutes {
	routes: StrutsRoute[]
}

async function loadServerRoutes(env: FormEnvironment):Promise<StrutsRoute[]>{
	return env.net.fetch("api/navigation/routes")
		.then((result:NetworkResponse) => result.json())
		.then((result:StrutsRoutes) => {
			return result.routes;
		});
}

/**
 * Normalize path to the full format (including context).
 * Let's say that context = "/intouch":
 * 	"/intouch/goThere.do" => "/intouch/goThere.do"
 * 	"/goThere.do" => "/intouch/goThere.do"
 * 	"goThere.do" => "/intouch/goThere.do"
 * 	"intouch/goThere.do" => "/intouch/goThere.do"
 */
export function toFullPath(path:string):string {
	if (!path) return window.Server.context || "/";		// return root of app
	if (window.Server.context) {
		if (!path.startsWith("/")) path = "/"+path;
		if (!path.startsWith(window.Server.context + "/")) path = window.Server.context + path;
		return path;
	} else {
		// Root context
		if (path.startsWith("/")) return path;
		return "/" + path;	// just append slash
	}
}

/**
 * Normalize path to the sub-context format (excluding context).
 * Let's say that context = "/intouch":
 * 	"/intouch/goThere.do" => "/goThere.do"
 * 	"/goThere.do" => "/goThere.do"
 * 	"goThere.do" => "/goThere.do"
 * 	"intouch/goThere.do" => "/goThere.do"
 */
export function toSubContextPath(path:string):string {
	if (!path) return "/";
	if (window.Server.context) {
		if (!path.startsWith("/")) path = "/"+path;
		const contextPrefix = window.Server.context + "/"
		if (path.startsWith(contextPrefix)) path = path.substring(window.Server.context.length);
		return path;
	} else {
		// Root context
		if (path.startsWith("/")) return path;
		return "/" + path;	// just append slash
	}
}

/**
 * Removes query string and hash from the path
 * @param path URL path
 */
export function removeParams(path:string):string {
	const qidx = path.indexOf('?');
	const hidx = path.lastIndexOf('#');
	const idx = qidx < 0 ? hidx : (hidx < 0 ? qidx : Math.min(qidx, hidx));
	if (idx >= 0) path = path.substring(0, idx);
	return path;
}

/** Extract hash fron URL path. Returns null if there isn't one */
export function getHash(path:string):string {
	if (!path) return null;
	const hidx = path.lastIndexOf('#');
	if (hidx >= 0) return path.substring(hidx+1);
	return null;
}

export function buildRouteNameFromPath(path:string):string {
	if (!path) return undefined;
	if (path.startsWith('/')) path = path.substring(1);
	if (path.endsWith('.do')) path = path.substring(0, path.length - 3);
	return path;
}

function buildRoutes(sr: StrutsRoute[]):RouteRecordRaw[] {
	const routes:RouteRecordRaw[] = [];
	const contextPath = window.Server.context;
	for (let r of sr) {
		const rt:RouteRecordRaw = {
			path: contextPath + r.path,
			name: buildRouteNameFromPath(r.path),
			component: () => resolvePageComponent(r.component, r.param),
			meta: { struts: r.component }
		};
		if (r.section) rt.meta.section = r.section ;
		if (r.bind) {
			rt.props = { ...r.bind };
		}
		routes.push(rt);
	}
	return routes;
}

export let routerPromise:Promise<Router>|null = null;
/** Flag if the app running in full page mode (there is <router-view> available to display routes) */
export let fullPageMode:boolean = false;
export let routes:RouteRecordRaw[] = [];	// not yet loaded
export let router:Router;

function asString(value:string|number|boolean):string {
	if (typeof value === "string") return value;
	return value == null ? "null" : value.toString();
}

/** @deprecated */
export function getAttParam(route:RouteLocationNormalizedLoaded, name: string):string {
	const qval = route.query[name];
	return Array.isArray(qval) ? qval[0] : qval;
}

/** @deprecated */
export function getIntAttParam(route:RouteLocationNormalizedLoaded, name: string):number {
	const strVal = getAttParam(route, name);
	if (strVal == null) return 0;
	return parseInt(strVal);
}

/**
 * This version accept query params in path string
 * @return true if the target path was Vue Router route and so there isn't full page reload
 **/
export function go(path: string, replace = false):boolean {
	if (path.startsWith("http://") || path.startsWith("https://")) {
		window.location.href = path;
		return false;
	}
	path = toFullPath(path);
    if (isRoute(path)) {
		router.push({ ...parseLocalUrl(path), replace });
		return true;
	} else {
		window.location.href = path;
		return false;
	}
}

/**
 * This version DOESN'T accept query params in path string and they have to be provided in the separate argument
 * @return true if the target path was Vue Router route and so there isn't full page reload
 **/
export function goParams(path: string, query?:LocationQueryRaw, replace = false):boolean {
	path = toFullPath(removeParams(path));
    if (isRoute(path)) {
		router.push({ path, query:query, replace });
		return true;
	} else {
		// Add params to full path
		if (query) {
			for (let k of Object.keys(query)) {
				path = urlParam(path, k, query[k]);
			}
		}
		window.location.href = path;
		return false;
	}
}

/** Breaks url to path and query */
export function parseLocalUrl(url: string): { path: string; query: Record<string, string> } {
	const base = 'http://dummy.base'; // required for relative URLs
	const u = new URL(url, base);
	const path = u.pathname;
	const query: Record<string, string> = {};

	u.searchParams.forEach((value, key) => {
		query[key] = value
	});

	return { path, query };
}

function routeMatch(mi: MenuItem, route: RouteLocationNormalizedLoaded, activeSection?:string): boolean {
	if (activeSection && activeSection === mi.name) return true;	// current section can be provided externally
	if (route.query.section && route.query.section === mi.name) return true;	// or from route query param
	if (route.meta.section && route.meta.section === mi.name) return true;	// or from route definitions
	// or determined by parsing URL
	if (isNav(mi)) {
		let parsed = parseEndpoint(mi.link);
		if (parsed.path !== route.path) return false;
		// Path is the same. All query params from mi must be also present in the route
		if (parsed.params.size > 0) {
			for (let paramName of parsed.params.keys()) {
				const paramValue = parsed.params.get(paramName);
				if (paramValue !== route.query[paramName]) return false;
			}
		}
		return true;
	}
	return false;
}

export function routeActiveSection(route: RouteLocationNormalizedLoaded, defaultActiveSection?: string):string {
	if (typeof route.query.section === "string") return route.query.section;
	if (typeof route.meta.section === "string") return route.meta.section;
	return defaultActiveSection;
}

export function findNavPath(sections:MenuItem[], route:RouteLocationNormalizedLoaded, activeSection?:string): MenuItem[] {
	for (let sec of sections) {
		if (hasChildren(sec)) {
			// search in the children
			let chPath = findNavPath(sec.children, route, activeSection);
			if (chPath.length > 0) {
				// Found in the children => this is "onpath" item
				chPath.splice(0, 0, sec);
				return chPath;
			}
		}
		if (routeMatch(sec, route, activeSection)) return [ sec ];
	}
	return [];
}

export function classifyNavSection(navPath: MenuItem[], current:MenuItem):MenuSelect {
	let i = 0;
	for (let mi of navPath) {
		if (mi === current) {
			return i === (navPath.length - 1) ? "target" : "path";
		}
	}
	return "";
}


let firstRoute = true;
let invokingPath:string|null = null;
let attributes:AppContextMap;

function navigationGuard(to, from, next) {
	// console.log("Navigation guard called");
	// console.dir(to);
	closeAll();	// close all open panels
	// console.log("Nav guard " + to.path);
	if (firstRoute) {
		firstRoute = false;
		let newPath:string = null;
		let newQuery = null;
		if (invokingPath) {
			const fullPath = window.Server.context + invokingPath;
			if (fullPath !== to.fullPath) newPath = fullPath;
		}
		if (attributes && !emptyMap(attributes)) {
			newQuery = { ...to.query, ...attributes };
		}
		if (newPath || newQuery) {
			next({
				path: (!newPath ? to.fullPath : newPath),
				query: (!newQuery ? to.query : newQuery)
			});
		} else {
			next();
		}
	} else {
		next();
	}
}

/**
 * This map of routes already contains full path (including context), e.g. /intouch/editParty.do
 */
let routeMap = new Set<string>();

/**
 * Test if the path is Vue route (true) or standard link (false)
 * @param path
 */
export function isRoute(path:string):boolean {
    if (!fullPageMode || path == null || path.startsWith("#")) return false;
    return routeMap.has(toFullPath(removeParams(path)));
}


function fillRouteMap() {
    const rm = new Set<string>();
    for (let r of routes) {
        if (r.path) rm.add(r.path);
    }
    routeMap = rm;
}

/**
 * Initialize router (async)
 * @param env environment
 * @param staticRoutes if you want to provide static routes, use this parameters. if omitted the routes will be loaded from the server
 * @param lastPath last action mapping path if the router is initialized from main.jsp (Struts action)
 * @param attrs extra attributes (will be appended to query)
 */
export function initRouter(env: FormEnvironment, staticRoutes?:RouteRecordRaw[], lastPath?:string|null, attrs?:AppContextMap, fullPage = false):Promise<Router> {
	attributes = attrs ?? {};
	invokingPath = lastPath;
	fullPageMode = fullPage;

	if (routerPromise == null) {
		if (staticRoutes) {
			routes = staticRoutes;
            fillRouteMap();
			router = createRouter({
				history: createWebHistory(),
				routes: routes
			});
			router.beforeEach(navigationGuard);
			routerPromise = Promise.resolve(router);
			return routerPromise;
		} else {
			routerPromise = new Promise((resolve, reject) => {
				loadServerRoutes(env)
					.then((strutRoutes) => {
						//console.log("Loaded " + strutRoutes.length + " struts routes");
						return buildRoutes(strutRoutes);
					})
					.then((rts) => {
						//console.dir(rts);
						routes = rts;
                        fillRouteMap();
						router = createRouter({
							history: createWebHistory(),
							routes: rts
						});
						router.beforeEach(navigationGuard);
						resolve(router);
					})
					.catch((reason) => reject(reason));
			});
		}
	}
	return routerPromise;
}
